import { useEffect, useRef, useState } from "react";

export default function AutocompleteInput({ value, onChange, options, placeholder, testId, onCreate, createLabel = "Crear" }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const term = (value || "").trim();
  const termLower = term.toLowerCase();
  const filtered = options
    .filter((option) => option && option.toLowerCase().includes(termLower))
    .filter((option) => option.toLowerCase() !== termLower)
    .slice(0, 6);
  const exactMatch = options.some((option) => option && option.toLowerCase() === termLower);
  const showCreate = Boolean(onCreate && term && !exactMatch && !creating);

  const pick = (option) => {
    onChange(option);
    setOpen(false);
    setHighlight(0);
  };

  const handleCreate = async () => {
    if (!onCreate || !term) return;
    setCreating(true);
    try {
      const created = await onCreate(term);
      if (typeof created === "string" && created) {
        onChange(created);
      }
    } finally {
      setCreating(false);
      setOpen(false);
      setHighlight(0);
    }
  };

  const totalItems = filtered.length + (showCreate ? 1 : 0);
  const handleKeyDown = (event) => {
    if (!open || totalItems === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) => (current + 1) % totalItems);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => (current - 1 + totalItems) % totalItems);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (highlight < filtered.length) pick(filtered[highlight]);
      else handleCreate();
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="autocomplete-wrapper">
      <input
        data-testid={testId}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(event) => { onChange(event.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {open && (filtered.length > 0 || showCreate) && (
        <ul className="autocomplete-list" data-testid={testId ? `${testId}-list` : undefined}>
          {filtered.map((option, index) => (
            <li
              key={option}
              data-testid={testId ? `${testId}-option-${index}` : undefined}
              className={index === highlight ? "active" : ""}
              onMouseDown={(event) => { event.preventDefault(); pick(option); }}
              onMouseEnter={() => setHighlight(index)}
            >
              {option}
            </li>
          ))}
          {showCreate && (
            <li
              key="__create"
              data-testid={testId ? `${testId}-create` : undefined}
              className={`autocomplete-create ${highlight === filtered.length ? "active" : ""}`}
              onMouseDown={(event) => { event.preventDefault(); handleCreate(); }}
              onMouseEnter={() => setHighlight(filtered.length)}
            >
              ✚ {createLabel} “{term}”
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
